import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ChatCircleDots,
  PaperPlaneRight,
  Plus,
  Trash,
  CircleNotch,
  Lightbulb,
  Robot,
  User as UserIcon,
} from '@phosphor-icons/react';


function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`message-${msg.role}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-blue-600 text-white' : 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white'
      }`}>
        {isUser ? <UserIcon size={16} weight="fill" /> : <Robot size={16} weight="fill" />}
      </div>
      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-slate-100 text-slate-800 rounded-tl-sm'
      }`}>
        {msg.content}
      </div>
    </div>
  );
}


export default function AIAssistantPage() {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    fetchSuggestions();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/ai-assistant/sessions`, { withCredentials: true });
      setSessions(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-assistant/suggested-questions`, { withCredentials: true });
      setSuggestions(res.data?.suggestions || []);
    } catch (e) {
      console.error(e);
    }
  };

  const openSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-assistant/sessions/${sessionId}/messages`, { withCredentials: true });
      setCurrentSession(res.data.session);
      setMessages(res.data.messages || []);
    } catch (e) {
      console.error(e);
    }
  };

  const newConversation = () => {
    setCurrentSession(null);
    setMessages([]);
    setInput('');
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    try {
      await axios.delete(`${API_URL}/api/ai-assistant/sessions/${sessionId}`, { withCredentials: true });
      if (currentSession?.id === sessionId) newConversation();
      fetchSessions();
    } catch (e) {
      console.error(e);
    }
  };

  const sendMessage = async (text) => {
    const messageText = (text || input).trim();
    if (!messageText || sending) return;
    setInput('');
    setSending(true);

    // Optimistic UI: add user message immediately
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await axios.post(`${API_URL}/api/ai-assistant/chat`, {
        message: messageText,
        session_id: currentSession?.id,
      }, { withCredentials: true });

      const { session_id, user_message, assistant_message } = res.data;
      // Replace temp + add assistant
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id).concat([user_message, assistant_message]));

      if (!currentSession) {
        // Refresh sessions and select the new one
        await fetchSessions();
        setCurrentSession({ id: session_id });
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || 'Error al procesar la pregunta';
      setMessages(prev => [...prev, {
        id: 'err-' + Date.now(),
        role: 'assistant',
        content: '⚠️ ' + detail,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="ai-assistant-page">
      {/* Sidebar of sessions */}
      <aside className="w-72 border-r border-slate-200 bg-slate-50 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <button
            onClick={newConversation}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg hover:opacity-90 transition-opacity font-semibold text-sm"
            data-testid="new-conversation-btn"
          >
            <Plus size={16} weight="bold" />
            Nueva conversación
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex justify-center py-6"><CircleNotch size={24} className="animate-spin text-violet-600" /></div>
          ) : sessions.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6 px-2">Aún no tienes conversaciones. Empieza una nueva.</p>
          ) : (
            sessions.map(s => (
              <div
                key={s.id}
                onClick={() => openSession(s.id)}
                className={`group flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer mb-1 transition-colors ${
                  currentSession?.id === s.id ? 'bg-violet-100 text-violet-900' : 'hover:bg-slate-200 text-slate-700'
                }`}
                data-testid={`session-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{s.title || 'Sin título'}</p>
                  <p className="text-[10px] text-slate-400">{new Date(s.updated_at).toLocaleDateString('es-ES')}</p>
                </div>
                <button
                  onClick={(e) => deleteSession(s.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded text-red-500"
                  data-testid={`delete-session-${s.id}`}
                >
                  <Trash size={12} />
                </button>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main chat area */}
      <main className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center">
            <Robot size={20} weight="fill" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Asistente IQES</h1>
            <p className="text-xs text-slate-500">Conectado al CRM en tiempo real · GPT-4o</p>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4">
                <ChatCircleDots size={32} weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Hola, soy tu Asistente IQES</h2>
              <p className="text-sm text-slate-500 max-w-md mb-6">
                Pregúntame cualquier cosa sobre el CRM, tus datos, low voltage, NEC code, cómo cotizar, o ideas para vender más.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-2xl">
                {suggestions.slice(0, 6).map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    className="text-left px-4 py-3 bg-slate-50 hover:bg-violet-50 hover:border-violet-200 border border-slate-200 rounded-lg text-sm text-slate-700 transition-colors flex items-start gap-2"
                    data-testid={`suggestion-${i}`}
                  >
                    <Lightbulb size={16} className="text-violet-500 flex-shrink-0 mt-0.5" weight="fill" />
                    <span>{q}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)
          )}
          {sending && (
            <div className="flex gap-3" data-testid="typing-indicator">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center">
                <Robot size={16} weight="fill" />
              </div>
              <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white">
          <div className="flex items-end gap-2 bg-slate-50 rounded-2xl border border-slate-200 px-4 py-2 focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Escribe tu pregunta... (Enter para enviar, Shift+Enter para nueva línea)"
              rows={1}
              className="flex-1 bg-transparent resize-none outline-none text-sm text-slate-800 max-h-32"
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || sending}
              className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-lg transition-shadow"
              data-testid="send-message-btn"
            >
              {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="fill" />}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 text-center mt-2">El asistente puede cometer errores. Verifica datos críticos.</p>
        </div>
      </main>
    </div>
  );
}
